import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useMemo } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';

import { useEstimationItemAssumptionQueryActions } from '@/api/hooks/estimationItemAssumptionQueryActions';
import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import type { CreateEstimationItemAssumptionRequest } from '@/api/schema/data-contracts';
import { Loader } from '@/components/atoms/Loader';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useMessages } from '@/hooks/messages';
import { reportEvent } from '@/services/crashReporting';

import { Button } from '../../atoms/Button';
import { DropdownFormField } from '../../atoms/DropdownFormField';
import { ModalStateless } from '../../ModalStateless';
import { assumptionCreationModalMessageDefinitions } from './messages';

interface AssumptionCreateFormModel {
  assumptionCategoryId: string | null;
  description: string;
}

interface AssumptionCreationModalProps {
  estimationItemId: string;
  estimationModelId: string;
  estimationCultureName: string;
  open: boolean;
  onClose: () => void;
}

export const AssumptionCreationModal: FC<AssumptionCreationModalProps> = ({
  estimationItemId,
  estimationModelId,
  estimationCultureName,
  open,
  onClose,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(assumptionCreationModalMessageDefinitions);

  const { useGetEstimationModel } = useEstimationModelQueryActions();
  const { data: estimationModel, isLoading: isLoadingEstimationModel } = useGetEstimationModel(
    estimationModelId,
    estimationCultureName,
  );

  const { useCreateEstimationItemAssumption } = useEstimationItemAssumptionQueryActions();
  const assumptionCreationMutation = useCreateEstimationItemAssumption();

  const options = useMemo(
    () =>
      estimationModel?.assumptionCategories.map((category) => ({
        value: category.id,
        label: category.name,
      })) || [],
    [estimationModel?.assumptionCategories],
  );

  const formModelInitial = useMemo<AssumptionCreateFormModel>(
    () => ({
      assumptionCategoryId: null,
      description: '',
    }),
    [],
  );

  const { control, handleSubmit, reset, getValues } = useForm<AssumptionCreateFormModel>({
    defaultValues: formModelInitial,
  });

  useEffect(() => {
    reset(formModelInitial);
  }, [open, reset, formModelInitial]);

  if (isLoadingEstimationModel) {
    return <Loader />;
  }

  if (!estimationModel) {
    throw new Error('Estimation model not found');
  }

  const onSubmit = (formModel: AssumptionCreateFormModel) => {
    const assumptionToCreate: CreateEstimationItemAssumptionRequest = {
      description: formModel.description ?? '',
      isValid: true,
      estimationModelAssumptionCategoryId: formModel.assumptionCategoryId,
    };
    assumptionCreationMutation.mutate({
      estimationItemId: estimationItemId,
      estimationAssumption: assumptionToCreate,
    });
    onClose();
  };

  // EWM-1174: a PROD user reported Save "cannot be activated" and the report left no trace - a
  // failed validation keeps the modal open and sends nothing. Record which rule failed (never the
  // text itself) so the next occurrence is visible in customEvents.
  const onInvalid = (errors: FieldErrors<AssumptionCreateFormModel>) => {
    reportEvent('assumption.create.invalid', {
      failedRules: Object.entries(errors)
        .map(([field, error]) => `${field}:${error?.type}`)
        .join(','),
      descriptionLength: getValues('description')?.length ?? 0,
    });
  };

  return (
    <ModalStateless
      title={messages.title()}
      open={open}
      onClose={onClose}
      data-testid='assumption-creation-modal'
      buttons={
        <>
          <Button
            variant='secondary'
            outlined
            onClick={onClose}
            data-testid='cancel-assumption-button'
          >
            {globalMessages.cancel()}
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit(onSubmit, onInvalid)}
            data-testid='save-assumption-button'
          >
            {globalMessages.save()}
          </Button>
        </>
      }
    >
      <form className='space-y-5' data-testid='assumption-form'>
        <div>
          <label htmlFor='assumptionCategoryId'>{messages.assumptionType()}</label>
          <DropdownFormField
            control={control}
            fieldPath='assumptionCategoryId'
            options={options}
            fullWidth
            data-testid='assumption-category-dropdown'
          />
        </div>
        <div>
          <label htmlFor='description'>{messages.description()}</label>
          <WysiwygFormTextField
            control={control}
            fieldPath='description'
            placeholder={messages.descriptionPlaceholder()}
            rules={{
              required: true,
              maxLength: entityFieldSettings.estimationItemAssumptionEntity.descriptionMaxLength,
            }}
            data-testid='assumption-description-input'
          />
        </div>
      </form>
    </ModalStateless>
  );
};
